/*------------------------------------------------*/
/* LIBRARIES
/*------------------------------------------------*/
import PropTypes from 'prop-types';
import {
  autocomplete,
  getAlgoliaResults,
} from '@algolia/autocomplete-js';
import algoliasearch from 'algoliasearch/lite';
import React, {
  createElement, Fragment, useEffect, useRef
} from 'react';
import { createRoot } from 'react-dom/client';
import _noop from 'lodash/noop';
import { useStaticQuery, graphql } from 'gatsby';
/*------------------------------------------------*/
/* INTERNAL DEPENDENCIES
/*------------------------------------------------*/
import Hit from './Hit';


const Autocomplete = (props) => {
  const {
    className,
    indexName,
    onItemClick,
    onSelectItem,
    onSubmit,
    ...rest
  } = props;
  let searchClient;

  const containerRef = useRef(null);
  const panelRootRef = useRef(null);
  const rootRef = useRef(null);

  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            algolia {
              appId,
              apiKey
            }
          }
        }
      }
    `
  );

  useEffect(() => {
    searchClient = algoliasearch(
      site.siteMetadata.algolia.appId,
      site.siteMetadata.algolia.apiKey
    );
  }, []);

  useEffect(() => {
    if (!containerRef.current || !searchClient) {
      return undefined;
    }

    const search = autocomplete({
      container: containerRef.current,
      renderer: { createElement, Fragment, render: () => {} },
      render: ({ children }, root) => {
        if (!panelRootRef.current || rootRef.current !== root) {
          rootRef.current = root;

          panelRootRef.current?.unmount();
          panelRootRef.current = createRoot(root);
        }

        panelRootRef.current.render(children);
      },
      openOnFocus: true,
      detachedMediaQuery: '(max-width: 680px)',
      getSources: ({ query }) => [{
        sourceId: 'help_articles', // This is just an ID.
        getItems: () => getAlgoliaResults({
          searchClient,
          queries: [{
            indexName,
            query,
          }],
        }),
        templates: {
          item: ({ item, components }) => (
            <Hit
              hit={item}
              components={components}
              onClick={onItemClick}
            />
          ),
        },
        // This is to get the item URL to be hadled by the arrow keys:
        // https://www.algolia.com/blog/ux/replicating-the-algolia-documentation-search-with-autocomplete/
        getItemUrl: (params) => {
          const { item } = params;
          return onSelectItem(item);
        },
      }],
      onSubmit: (params) => {
        onSubmit(params.state.query);
      },
      ...rest,
    });

    return () => {
      search.destroy();
    };
  }, []);

  return <div className={className} ref={containerRef} />;
};

/*------------------------------------------------*/
/* PROP TYPES
/*------------------------------------------------*/
Autocomplete.defaultProps = {
  className: 'autocomplete-wrapper',
  indexName: 'help_center',
  placeholder: 'Search',
  onItemClick: _noop,
  onSelectItem: _noop,
  onSubmit: _noop,
};
Autocomplete.propTypes = {
  // Optional props:
  className: PropTypes.string,
  indexName: PropTypes.string,
  placeholder: PropTypes.string,
  // Optional methods:
  onItemClick: PropTypes.func,
  onSelectItem: PropTypes.func, // With the keyboard.
  onSubmit: PropTypes.func,
};

/*------------------------------------------------*/
/* EXPORTS
/*------------------------------------------------*/
export default Autocomplete;
